import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Plus, X, Users, Calendar, Clock, MapPin, DollarSign, Eye, CheckCircle, Pencil, ClipboardCheck, ThumbsUp, ThumbsDown, Music } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { formatDate } from '../utils/formatters';
import ConfirmModal from '../components/ConfirmModal';
import { sessionBasePath, matchesSessionActivity } from '../utils/sessionNavigation';

export default function OpenPlays({ activityType = 'OpenPlay' }) {
  const isStepDance = activityType === 'StepDance';
  const sessionLabel = isStepDance ? 'Step Dance' : 'Open Play';
  const participant = isStepDance ? 'dancer' : 'player';
  const basePath = sessionBasePath(activityType);
  const SessionIcon = isStepDance ? Music : Trophy;
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const [dateFilter, setDateFilter] = useState('all');
  const [approvalTarget, setApprovalTarget] = useState(null);
  const [approvalCourts, setApprovalCourts] = useState([]);
  const [selectedCourtIds, setSelectedCourtIds] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const res = await api.get('/openplays', { params: { status: 'all' } });
      setTournaments(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }

  async function handleCancel() {
    if (!cancelTarget) return;
    try {
      await api.delete(`/openplays/${cancelTarget.id}`);
      setTournaments((prev) => prev.filter((t) => t.id !== cancelTarget.id));
      toast.success(`${sessionLabel} cancelled`);
    } catch { toast.error('Failed to cancel'); }
    setCancelTarget(null);
  }

  async function handleComplete() {
    if (!completeTarget || completing) return;
    setCompleting(true);
    try {
      await api.post(`/openplays/${completeTarget.id}/complete`);
      setTournaments((prev) => prev.map((t) => t.id === completeTarget.id ? { ...t, status: 'Completed' } : t));
      toast.success(`${sessionLabel} completed`);
      setCompleteTarget(null);
    } catch (error) { toast.error(error?.response?.data?.message || 'Failed to complete'); }
    finally { setCompleting(false); }
  }

  const errorMessage = (error, fallback) =>
    error?.response?.data?.message || error?.response?.data?.error || fallback;

  async function openApproval(tournament) {
    setReviewLoading(true);
    try {
      const response = await api.get(`/venues/${tournament.venueId}/courts`);
      const courts = (Array.isArray(response.data) ? response.data : []).filter((court) => court.isActive !== false);
      setApprovalCourts(courts);
      setSelectedCourtIds([]);
      setApprovalTarget(tournament);
    } catch (error) {
      toast.error(errorMessage(error, 'Unable to load the venue courts'));
    } finally {
      setReviewLoading(false);
    }
  }

  async function handleApprove() {
    if (!approvalTarget || selectedCourtIds.length === 0) {
      toast.error(isStepDance ? 'Select at least one venue space' : 'Select at least one court');
      return;
    }
    setReviewLoading(true);
    try {
      await api.post(`/openplays/${approvalTarget.id}/approve`, { courtIds: selectedCourtIds });
      setTournaments((previous) => previous.map((item) =>
        item.id === approvalTarget.id
          ? { ...item, status: 'Active', courtNames: approvalCourts.filter((court) => selectedCourtIds.includes(court.id)).map((court) => court.name) }
          : item));
      toast.success(`${sessionLabel} approved and published`);
      setApprovalTarget(null);
    } catch (error) {
      toast.error(errorMessage(error, 'Unable to approve this request'));
    } finally {
      setReviewLoading(false);
    }
  }

  async function handleReject() {
    if (!rejectTarget || rejectionReason.trim().length < 3) {
      toast.error('Enter a brief reason for the player');
      return;
    }
    setReviewLoading(true);
    try {
      await api.post(`/openplays/${rejectTarget.id}/reject`, { reason: rejectionReason.trim() });
      setTournaments((previous) => previous.map((item) =>
        item.id === rejectTarget.id
          ? { ...item, status: 'Rejected', rejectionReason: rejectionReason.trim() }
          : item));
      toast.success(`${sessionLabel} request rejected`);
      setRejectTarget(null);
      setRejectionReason('');
    } catch (error) {
      toast.error(errorMessage(error, 'Unable to reject this request'));
    } finally {
      setReviewLoading(false);
    }
  }

  function formatTime(t) {
    if (!t) return '';
    const [h, m] = (typeof t === 'string' ? t : '').split(':').map(Number);
    if (isNaN(h)) return t;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : (h > 12 ? h - 12 : h);
    return `${h12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
  }

  function getStatusBadge(status) {
    const s = (status || '').toLowerCase();
    if (s === 'active') return 'bg-[#E0F7F4] text-[#56B7AC]';
    if (s === 'pendingapproval') return 'bg-[#FFF7D6] text-[#9A6A00]';
    if (s === 'rejected') return 'bg-[#FDF1EB] text-[#D46C4E]';
    if (s === 'cancelled') return 'bg-[#FDF1EB] text-[#D46C4E]';
    if (s === 'completed') return 'bg-[#E8F4FD] text-[#264D59]';
    return 'bg-[#F1F5F9] text-[#6B7280]';
  }

  const activitySessions = tournaments.filter(t => matchesSessionActivity(t, activityType));
  const filtered = activitySessions.filter((t) => {
    if (statusFilter !== 'all' && (t.status || '').toLowerCase() !== statusFilter) return false;
    if (dateFilter !== 'all') {
      const d = new Date(t.date);
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (dateFilter === 'today') {
        if (d < startOfDay || d >= new Date(startOfDay.getTime() + 86400000)) return false;
      }
      if (dateFilter === 'month') {
        if (d.getFullYear() !== now.getFullYear() || d.getMonth() !== now.getMonth()) return false;
      }
    }
    return true;
  });
  const pending = activitySessions.filter((t) => (t.status || '').toLowerCase() === 'pendingapproval');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#264D59] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">{sessionLabel}</h1>
          <p className="text-sm text-[#6B7280]">Manage sessions across your venues</p>
        </div>
        <button onClick={() => navigate(`${basePath}/new`)} className="btn-primary">
          <Plus className="h-4 w-4 mr-1" /> Create {sessionLabel}
        </button>
      </div>

      {pending.length > 0 && (
        <section className="rounded-2xl border border-[#E7D89B] bg-[#FFFDF4] p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#FFF2B8] text-[#8A6500]">
                <ClipboardCheck className="h-5 w-5" />
              </span>
              <div>
                <h2 className="font-semibold text-[#1F2937]">{isStepDance ? 'Dance session requests awaiting approval' : 'Player requests awaiting approval'}</h2>
                <p className="text-xs text-[#6B7280]">Review the schedule and assign courts before publishing.</p>
              </div>
            </div>
            <span className="rounded-full bg-[#264D59] px-2.5 py-1 text-xs font-bold text-white">{pending.length}</span>
          </div>
          <div className="grid gap-3 xl:grid-cols-2">
            {pending.map((request) => (
              <div key={request.id} className="rounded-xl border border-[#EEE5C4] bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-[#1F2937]">{request.name}</p>
                    <p className="mt-0.5 text-xs text-[#6B7280]">Requested by {request.submittedByName || (isStepDance ? 'a FREAC organiser' : 'a FREAC player')}</p>
                  </div>
                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${getStatusBadge(request.status)}`}>Pending approval</span>
                </div>
                <div className="mt-3 grid gap-1.5 text-xs text-[#6B7280] sm:grid-cols-2">
                  <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{request.venueName}</span>
                  <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{formatDate(request.date)}</span>
                  <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{formatTime(request.startTime)} — {formatTime(request.endTime)}{request.endTime < request.startTime ? ' (next day)' : ''}</span>
                  <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />Up to {request.maxPlayers} {participant}s</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={() => navigate(`${basePath}/${request.id}`)} className="btn-outline text-xs px-3 py-1.5"><Eye className="h-3.5 w-3.5 mr-1" />View</button>
                  <button disabled={reviewLoading} onClick={() => openApproval(request)} className="btn-primary text-xs px-3 py-1.5"><ThumbsUp className="h-3.5 w-3.5 mr-1" />Approve</button>
                  <button onClick={() => { setRejectTarget(request); setRejectionReason(''); }} className="btn-outline text-xs px-3 py-1.5 text-[#D46C4E] border-[#D46C4E]/30 hover:bg-[#FDF1EB]"><ThumbsDown className="h-3.5 w-3.5 mr-1" />Reject</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {filtered.length === 0 && !loading && (
        <div className="text-center py-16">
          <SessionIcon className="mx-auto h-12 w-12 text-[#94A3B8]" />
          <p className="mt-4 text-[#6B7280]">No {sessionLabel} sessions match these filters.</p>
        </div>
      )}

      <div className="card p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1 bg-[#F1F5F9] p-1 rounded-[12px]">
            {[
              { key: 'all', label: 'All' },
              { key: 'active', label: 'Active' },
              { key: 'pendingapproval', label: `Pending (${pending.length})` },
              { key: 'rejected', label: 'Rejected' },
              { key: 'cancelled', label: 'Cancelled' },
              { key: 'completed', label: 'Completed' },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${statusFilter === f.key ? 'bg-[#264D59] text-white font-semibold' : 'text-[#6B7280] hover:text-[#1F2937] hover:bg-white'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="w-px h-6 bg-[#E2E8F0]" />
          <div className="flex gap-1 bg-[#F1F5F9] p-1 rounded-[12px]">
            {[
              { key: 'all', label: 'All Dates' },
              { key: 'today', label: 'Today' },
              { key: 'month', label: 'This Month' },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setDateFilter(f.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${dateFilter === f.key ? 'bg-[#264D59] text-white font-semibold' : 'text-[#6B7280] hover:text-[#1F2937] hover:bg-white'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <p className="text-xs text-[#6B7280]">{filtered.length} session{filtered.length !== 1 ? 's' : ''}</p>

      <div className="space-y-3">
        {filtered.map((t) => (
          <div key={t.id} className="card p-4 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex min-w-0 flex-wrap items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#F1F5F9] flex items-center justify-center">
                <SessionIcon className="h-5 w-5 text-[#264D59]" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-[#1F2937] font-semibold text-sm">{t.name}</p><p className="text-xs text-[#6B7280]">{t.activityType === 'StepDance' ? 'Step Dance' : 'Pickleball Open Play'}</p>
                  <span className={`px-2 py-0.5 text-xs rounded-[30px] font-medium ${getStatusBadge(t.status)}`}>{t.hasEnded && t.status === 'Active' ? 'Ended' : t.status || 'Active'}</span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-[#6B7280]">
                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{t.venueName}</span>
                  <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(t.date)}</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatTime(t.startTime)} — {formatTime(t.endTime)}{t.endTime < t.startTime ? ' (next day)' : ''}</span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-[#6B7280]">
                  <span className="flex items-center gap-1"><Users className="h-3 w-3" />{t.playerCount} / {t.maxPlayers} {participant}s</span>
                  {t.pricePerPlayer > 0 && <span className="flex items-center gap-1"><DollarSign className="h-3 w-3" />PHP {t.pricePerPlayer.toFixed(0)}</span>}
                </div>
              </div>
              {isStepDance && <div className="mt-2 w-full text-xs text-[#6B7280] space-y-1"><p>Instructor: {t.instructorName || 'To be confirmed'}</p><p>Dance space: {t.blocksCourtBookings === false ? 'Dedicated dance space' : (t.courtNames || []).join(', ') || 'Reserved venue space'}</p><p>Equipment: {t.equipmentNotes || 'Bring your own'}{Number(t.equipmentRentalFee) > 0 ? ` · Rental PHP ${Number(t.equipmentRentalFee).toFixed(2)} per dancer` : ''}</p></div>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => navigate(`${basePath}/${t.id}`)} className="btn-outline text-xs px-3 py-1.5">
                <Eye className="h-3.5 w-3.5 mr-1" />View
              </button>
              {t.hasEnded && <p className="mb-2 text-sm text-gray-600">This session has ended (Philippine time). {isStepDance ? 'Dancer' : 'Player'} joining is closed; management remains available.</p>}
              {t.status === 'Active' && (
                <>
                  <button onClick={() => navigate(`${basePath}/${t.id}?addPlayer=1`)} className="btn-outline text-xs px-3 py-1.5">
                    <Plus className="h-3.5 w-3.5 mr-1" />Add {participant}
                  </button>
                  <button onClick={() => navigate(`${basePath}/${t.id}/edit`)} className="btn-outline text-xs px-3 py-1.5">
                    <Pencil className="h-3.5 w-3.5 mr-1" />Edit
                  </button>
                  <button onClick={() => setCompleteTarget({ id: t.id, name: t.name })} className="btn-outline text-xs px-3 py-1.5 text-[#56B7AC] border-[#56B7AC] hover:bg-[#E0F7F4]">
                    <CheckCircle className="h-3.5 w-3.5 mr-1" />Complete
                  </button>
                  <button onClick={() => setCancelTarget({ id: t.id, name: t.name })} className="btn-outline text-xs px-3 py-1.5 text-[#D46C4E] border-[#D46C4E]/30 hover:bg-[#FDF1EB]">
                    <X className="h-3.5 w-3.5 mr-1" />Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <ConfirmModal
        open={!!completeTarget}
        title={`Complete ${sessionLabel}?`}
        message={`Complete "${completeTarget?.name}"? This will close the session to new ${participant}s and payments. Check the roster and pending payment links first.`}
        confirmLabel={completing ? 'Completing…' : 'Complete session'}
        cancelLabel="Keep active"
        onConfirm={handleComplete}
        onCancel={() => { if (!completing) setCompleteTarget(null); }}
      />

      <ConfirmModal
        open={!!cancelTarget}
        title={`Cancel ${sessionLabel}?`}
        message={`Are you sure you want to cancel "${cancelTarget?.name}"?`}
        confirmLabel={`Cancel ${sessionLabel}`}
        cancelLabel="Go Back"
        variant="danger"
        onConfirm={handleCancel}
        onCancel={() => setCancelTarget(null)}
      />

      {approvalTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0F2630]/55 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-bold text-[#1F2937]">Approve “{approvalTarget.name}”</h2>
            <p className="mt-1 text-sm text-[#6B7280]">Assign the venue space reserved for this {sessionLabel}. Availability is checked again when you approve.</p>
            <div className="mt-4 max-h-64 space-y-2 overflow-y-auto">
              {approvalCourts.length === 0 ? (
                <p className="rounded-xl bg-[#FDF1EB] p-3 text-sm text-[#D46C4E]">This venue has no active courts. Add a court before approving.</p>
              ) : approvalCourts.map((court) => (
                <label key={court.id} className="flex cursor-pointer items-center gap-3 rounded-xl border border-[#E2E8F0] p-3 hover:bg-[#F8FAFC]">
                  <input
                    type="checkbox"
                    checked={selectedCourtIds.includes(court.id)}
                    onChange={(event) => setSelectedCourtIds((current) => event.target.checked
                      ? [...current, court.id]
                      : current.filter((id) => id !== court.id))}
                    className="h-4 w-4 accent-[#0E949B]"
                  />
                  <span className="text-sm font-medium text-[#1F2937]">{court.name}</span>
                </label>
              ))}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button className="btn-outline" onClick={() => setApprovalTarget(null)} disabled={reviewLoading}>Cancel</button>
              <button className="btn-primary" onClick={handleApprove} disabled={reviewLoading || selectedCourtIds.length === 0}>
                {reviewLoading ? 'Approving…' : 'Approve & Publish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0F2630]/55 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-bold text-[#1F2937]">Reject “{rejectTarget.name}”</h2>
            <p className="mt-1 text-sm text-[#6B7280]">Give the {participant} a clear reason so they can update and submit a new request.</p>
            <textarea
              value={rejectionReason}
              onChange={(event) => setRejectionReason(event.target.value)}
              maxLength={500}
              rows={4}
              placeholder="Example: The selected time is already reserved for a private event."
              className="mt-4 w-full rounded-xl border border-[#CBD5E1] px-3 py-2 text-sm outline-none focus:border-[#0E949B] focus:ring-2 focus:ring-[#0E949B]/15"
            />
            <p className="mt-1 text-right text-xs text-[#94A3B8]">{rejectionReason.length}/500</p>
            <div className="mt-5 flex justify-end gap-2">
              <button className="btn-outline" onClick={() => setRejectTarget(null)} disabled={reviewLoading}>Cancel</button>
              <button className="btn-outline text-[#D46C4E] border-[#D46C4E]/30 hover:bg-[#FDF1EB]" onClick={handleReject} disabled={reviewLoading || rejectionReason.trim().length < 3}>
                {reviewLoading ? 'Rejecting…' : 'Reject Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
